import React, { useEffect, useState, useMemo, useCallback } from "react";
import useHotelActions from "../hooks/useHotelActions";
import { getServicesByHotelAndType, getBookableItemsByHotel, getBookableItemHotel } from "../actions/servicesActions";
// import { useLocalStorage } from '../hooks/useLocalStorage';
import { useLocalStorage } from "@penseapp/uselocalstorage";
import { getCDNImage } from '../globals';
import { isHostz, isHostzMyde } from "../components/CheckIn/hotelConditions";

import { API, graphqlOperation } from "aws-amplify";

const HotelContext = React.createContext({ data: {} })

const safeJsonParse = (value, fallback = {}) => {
    if (!value) return fallback;
    if (typeof value !== "string") return value;
    try {
        return JSON.parse(value);
    } catch (error) {
        return fallback;
    }
}

const hotel_onCreateEventMark = /* GraphQL */ `
  subscription OnCreateEventMark($hotelId: String!) {
    onCreateEventMark(filter: {
        entity: {
            eq: "hotel"
        },
        entityId: {
            eq: $hotelId
        }
    }) {
        entity
        entityId
        topic
    }
  }
`;

export const PWA_PREVIEW_SYNC_MESSAGE = "VICO_PWA_PREVIEW_SYNC";
export const PWA_PREVIEW_READY_MESSAGE = "VICO_PWA_PREVIEW_READY";

const PWA_PREVIEW_MODE = "backoffice";
const PWA_PREVIEW_MODE_STORAGE_KEY = "ViCo::pwa-preview-mode";
const PWA_PREVIEW_PARENT_ORIGIN_STORAGE_KEY = "ViCo::pwa-preview-parent-origin";

const getPreviewBridgeConfig = () => {
    if (typeof window === "undefined") {
        return {
            enabled: false,
            parentOrigin: "",
        };
    }

    const searchParams = new URLSearchParams(window.location.search);
    const previewModeParam = searchParams.get("previewMode");
    const parentOriginParam = searchParams.get("previewParentOrigin");

    let storedPreviewMode = "";
    let storedParentOrigin = "";

    try {
        storedPreviewMode = window.sessionStorage.getItem(PWA_PREVIEW_MODE_STORAGE_KEY) || "";
        storedParentOrigin = window.sessionStorage.getItem(PWA_PREVIEW_PARENT_ORIGIN_STORAGE_KEY) || "";
    } catch (error) {
        storedPreviewMode = "";
        storedParentOrigin = "";
    }

    const enabled = previewModeParam === PWA_PREVIEW_MODE || storedPreviewMode === PWA_PREVIEW_MODE;
    const parentOrigin = parentOriginParam || storedParentOrigin || "";

    try {
        if (enabled) {
            window.sessionStorage.setItem(PWA_PREVIEW_MODE_STORAGE_KEY, PWA_PREVIEW_MODE);
        }
        if (parentOrigin) {
            window.sessionStorage.setItem(PWA_PREVIEW_PARENT_ORIGIN_STORAGE_KEY, parentOrigin);
        }
    } catch (error) {
        // Ignore sessionStorage errors in preview mode.
    }

    return {
        enabled,
        parentOrigin,
    };
}

function HotelProvider({ children }) {
    const [lastEvent, setLastEvent] = useState(null);
    const [eventVersion, setEventVersion] = useState(0);

    const [loading, setLoading] = useState(false)
    const [data, setData] = useLocalStorage("ViCo::hotel-v2", null, window.location.origin.indexOf('editing') === -1 ? 10 * 60 : 0.5);
    const [folderNavigation, setFolderNavigation] = useState(true);
    const [error, setError] = useState(null)
    const { getHotelByCode, getHotelById } = useHotelActions()
    const [previewBridgeConfig] = useState(() => getPreviewBridgeConfig());
    const [previewDraft, setPreviewDraft] = useState(null);
    const previewMode = previewBridgeConfig.enabled;
    const previewParentOrigin = previewBridgeConfig.parentOrigin;

    useEffect(() => {
        if (typeof document === "undefined") {
            return undefined;
        }

        const previewClassName = "pwa-preview-mode";

        if (previewMode) {
            document.body.classList.add(previewClassName);
        } else {
            document.body.classList.remove(previewClassName);
        }

        return () => {
            document.body.classList.remove(previewClassName);
        };
    }, [previewMode]);

    useEffect(() => {
        if (!data?.id) return;

        const subscription = API.graphql(graphqlOperation(hotel_onCreateEventMark, {
            "hotelId": data.id
        })).subscribe({
            next: ({ value }) => {
                const newEvent = value?.data?.onCreateEventMark;
                setLastEvent(newEvent);
                setEventVersion((v) => v + 1); // 🔥 incrementa en cada evento
            },
            error: (err) => console.error("❌ Error en subscription:", err),
        });

        return () => subscription.unsubscribe();
    }, [ data?.id ]);

    useEffect(() => {
        const domain = window.location.hostname;

        if (data?.name) {
            document.title = data.name;
        }

        let hotelCode = null;

        if (domain === "app.hotelcostagalana.com") {
            setFolderNavigation(false);
            hotelCode = "costa-galana";
        }
        if (domain === "app.hotelgrandbrizobelair.com") {
            setFolderNavigation(false);
            hotelCode = "grand-brizo-bel-air";
        }
        if (domain === "local.hotel-dev-matias.com") {
            setFolderNavigation(false);
            hotelCode = "dev-matias";
        }
        if (domain === "app.hotelbrizosalta.com") {
            setFolderNavigation(false);
            hotelCode = "brizosalta";
        }
        if (domain === "app.hotelgrandbrizocomahue.com") {
            setFolderNavigation(false);
            hotelCode = "comahue";
        }
        if (domain === "app.hotelbrizoneuquen.com") {
            setFolderNavigation(false);
            hotelCode = "brizo-neuquen";
        }
        if (domain === "app.cgbeach.com.ar") {
            setFolderNavigation(false);
            hotelCode = "cg-beach";
        }
        if (domain === "app.hotelunitsantarosa.com") {
            setFolderNavigation(false);
            hotelCode = "santa-rosa";
        }
        if (domain === "app.hotelgrandbrizolaplata.com") {
            setFolderNavigation(false);
            hotelCode = "grand-brizo-la-plata";
        }
        if (domain === "app.hoteliruna.com") {
            setFolderNavigation(false);
            hotelCode = "iruna";
        }
        if (domain === "app.hotelriviera.com.ar") {
            setFolderNavigation(false);
            hotelCode = "riviera";
        }
        if (domain === "app.hotelgrandbrizobuenosaires.com") {
            setFolderNavigation(false);
            hotelCode = "grand-brizo-buenos-aires";
        }
        if (domain === "app.loscauquenes.com") {
            setFolderNavigation(false);
            hotelCode = "los-cauquenes";
        }

        /* --------- */



        if (!hotelCode) {
            hotelCode = window.location.pathname.split('/')[1];
        }
        if (!hotelCode) {
            setData(null)
            setError('Hotel Code is Empty')
            return;
        }

        if (!data || data.code !== hotelCode) {

            let redirectHotelCode = false
            if (hotelCode === "hotel-dev-mock" || hotelCode === "dev-matias-mock") {
                redirectHotelCode = "dev-matias"
            }
            if (hotelCode === "betahouse" || hotelCode === "aandg") {
                redirectHotelCode = "sigma"
            }
            if (redirectHotelCode) {
               window.location.href = window.location.href.replace(hotelCode, redirectHotelCode);
            }
            console.log('19dic - data', data)
            console.log('19dic - data.code', data?.code)
            console.log('19dic - hotelCode', hotelCode)
            setLoading(true)
            getHotelByCode(hotelCode).then(r => {
                if (r) {
                    const _cacheVersion = Date.now();
                    const payload = { ...r, cacheVersionInitial: _cacheVersion, cacheVersion: _cacheVersion };
                    console.log('19dic - payload', payload);

                    setData(payload);
                    document.title = r.name;
                    document.querySelector('#document-title').text = r.name;
                } else {
                    setError("Hotel '" + hotelCode + "' not exists")
                }
                setLoading(false)
            })
        } else if (data?.cacheVersionInitial !== data?.cacheVersion) {
            refreshById();
        }

        if (window.location.search.indexOf('refresh_cache') !== -1) {
            refreshCache();
        }

        // Por ahora cada 5 minuto refresca cambios en hotel... 
        // luego lo haremos reactivo
        // const intervalId = setInterval(() => {
        //     refreshById();
        // }, 60 * 10 * 1000);

        // return () => {
        //     clearInterval(intervalId);
        // };

    }, [])

    useEffect(() => {
        if (!previewMode || !previewParentOrigin || window.parent === window) {
            return undefined;
        }

        const handlePreviewMessage = (event) => {
            if (event.origin !== previewParentOrigin) {
                return;
            }

            if (event.data?.type !== PWA_PREVIEW_SYNC_MESSAGE) {
                return;
            }

            setPreviewDraft(event.data.payload || null);
        };

        window.addEventListener("message", handlePreviewMessage);
        window.parent.postMessage(
            {
                type: PWA_PREVIEW_READY_MESSAGE,
                version: 1,
            },
            previewParentOrigin
        );

        return () => {
            window.removeEventListener("message", handlePreviewMessage);
        };
    }, [previewMode, previewParentOrigin]);

    const refreshById = useCallback(() => {
        if (data?.id) {
            getHotelById(data.id).then(r => {
                if (data.id === r.id) {
                    setData({ ...data, ...r, cacheVersionInitial: data.cacheVersion });
                    document.title = r.name;
                    document.querySelector('#document-title').text = r.name;
                }
            })
        }
    }, [data])

    /** Refresca placeholders del hotel aunque el resto venga de localStorage (TTL 10 min). */
    useEffect(() => {
        if (!data?.id || previewMode) {
            return undefined;
        }
        let cancelled = false;
        getHotelById(data.id).then((fresh) => {
            if (cancelled || !fresh?.id || fresh.id !== data.id) {
                return;
            }
            const nextPlaceholders = fresh?.settings?.placeholders;
            if (!nextPlaceholders || typeof nextPlaceholders !== "object") {
                return;
            }
            setData((prev) => {
                if (!prev || prev.id !== fresh.id) {
                    return prev;
                }
                const prevPh = prev.settings?.placeholders || {};
                if (JSON.stringify(prevPh) === JSON.stringify(nextPlaceholders)) {
                    return prev;
                }
                return {
                    ...prev,
                    settings: {
                        ...(prev.settings || {}),
                        ...(fresh.settings || {}),
                        placeholders: nextPlaceholders,
                    },
                };
            });
        });
        return () => {
            cancelled = true;
        };
    }, [data?.id, previewMode, getHotelById])

    const getUrlPath = useCallback((path) => {
        if (path === '') path = "/"
        if (path[0] !== '/') {
            path = '/' + path;
        }
        return folderNavigation ? `/${data.code}${path}` : `${path}`
    }, [data?.code, folderNavigation])

    const mergedData = useMemo(() => {
        if (!data) {
            return data;
        }

        if (!previewDraft?.hotel) {
            return data;
        }

        return {
            ...data,
            ...previewDraft.hotel,
            settings: {
                ...(data.settings || {}),
                ...(previewDraft.hotel.settings || {}),
            },
        };
    }, [data, previewDraft]);

    const normalizedSections = useMemo(() => {
        const sourceSections = mergedData?.settings?.sections || [];
        const clonedSections = sourceSections.map((section) => ({
            ...section,
            extras: safeJsonParse(section?.extras, {}),
            pages: [],
        }));

        const sectionsMap = clonedSections.reduce((acc, section) => {
            acc[section.code] = section;
            return acc;
        }, {});

        clonedSections.forEach((section) => {
            if (!section?.parent || section.parent === section.code) {
                section.parent = null;
                return;
            }

            const parentSection = sectionsMap[section.parent];
            if (!parentSection) {
                section.parent = null;
                return;
            }

            parentSection.pages.push(section);
        });

        return clonedSections;
    }, [mergedData?.settings?.sections])

    const sectionsByCode = useMemo(() => {
        return normalizedSections.reduce((acc, section) => {
            acc[section.code] = section;
            return acc;
        }, {})
    }, [normalizedSections])

    const getServicesByType = useCallback((type) => {
        // TODO:: parche para pool service
        console.log('2012 - type', type)
        if (type === "poolService") {
            type = "roomService";
        }
        return getServicesByHotelAndType(data.id, type)
    }, [data]);

    const getBookableItems = useCallback((type) => {
        return getBookableItemsByHotel(data.id)
    }, [data]);

    const getBookableItem = useCallback((id) => {
        return getBookableItemHotel(id)
    }, [data]);




    // const clearWebpush = useCallback(() => {
    //     setData({ ...data, WebPush: null })
    // }, [ data ])

    useEffect(() => {
        if (!data) return null;

        if (navigator.serviceWorker) {
            navigator.serviceWorker.controller?.postMessage({
                type: 'HOTEL_INFO',
                payload: {
                    id: data.id,
                    code: data.code,
                    name: data.name,
                    icon: data.settings?.icon ? getCDNImage(data.settings.icon) : null
                }
            });
        }

    }, [data])


    const safariCheckRemotePermission = function (permissionData, userData) {
        console.log('permissionData', permissionData);
        if (permissionData.permission === 'default') {
            // This is a new web service URL and its validity is unknown.
            console.log('userData', userData);
            window.safari.pushNotification.requestPermission(
                // 'https://atipiko.com/vico-push-notifications', // The web service URL.
                'https://iplsgupdjc.execute-api.eu-west-1.amazonaws.com/dev',
                'web.com.vicomanager.pwa',     // The Website Push ID.
                userData, // Data that you choose to send to your server to help you identify the user.
                safariCheckRemotePermission         // The callback function.
            );
        }
        else if (permissionData.permission === 'denied') {
            // The user said no.
        }
        else if (permissionData.permission === 'granted') {
            // The web service URL is a valid push provider, and the user said yes.
            // permissionData.deviceToken is now available to use.
        }
    };

    const refreshCache = useCallback(() => {
        console.log('31oct - REFRESHHH CACHE')
        if (navigator.serviceWorker) {
            navigator.serviceWorker.controller?.postMessage({
                type: 'CLEAR_CACHE'
            });
        }
        if (data?.cacheVersion) {
            console.log('31oct - REFRESHHH CACHE SIII')
            setData({ ...data, cacheVersion: Date.now() });
        }
    }, [data]);

    // const extra = useMemo(() => {
    //     if (!data?.extra) return {};
    //     try {
    //         return JSON.parse(data.extra);
    //     } catch (error) {
    //         console.error("Failed to parse extras:", error);
    //         return {};
    //     }
    // }, [data]);

    const isRoyal = useMemo(
        () => (
            data?.id === "__511e8df9-672d-42b1-9711-d82beb7baf64"         // dev
        ),
        [data?.id]
    );
    const isCostaGalana = useMemo(
        () => (
            data?.id === "1de880e5-bfb6-4518-bd1b-095443bbc58b" ||      // prd
            data?.id === "8b91c3ba-6ef0-4667-805b-30b51f378ca5"         // dev
        ),
        [data?.id]
    );
    const isBelAir = useMemo(
        () => (
            data?.id === "c07d2d8a-9db1-412c-b24d-c0fcc3c5e7a0" ||      // prd
            data?.id === "__511e8df9-672d-42b1-9711-d82beb7baf64"         // dev
        ),
        [data?.id]
    );
    const isComahue = useMemo(
        () => (
            data?.id === "c0bb7b5c-cd44-4288-98f1-4868ea4e5c11" ||      // prd
            data?.id === "__511e8df9-672d-42b1-9711-d82beb7baf64"         // dev
        ),
        [data?.id]
    );
    const isBrizoNeuquen = useMemo(
        () => (
            data?.id === "f33a83b9-6284-46e6-8469-b5b17de2c9ba" ||      // prd
            data?.id === "__511e8df9-672d-42b1-9711-d82beb7baf64"         // dev
        ),
        [data?.id]
    );
    const isSantaRosa = useMemo(
        () => (
            data?.id === "2645e6bf-25cc-4d87-8bc4-80b01f026fea" ||      // prd
            data?.id === "__511e8df9-672d-42b1-9711-d82beb7baf64"         // dev
        ),
        [data?.id]
    );
    const isGrandBrizoBuenosAires = useMemo(
        () => (
            data?.id === "365edffa-c56e-4918-a16a-305452315e8d" ||      // prd
            data?.id === "__511e8df9-672d-42b1-9711-d82beb7baf64"         // dev
        ),
        [data?.id]
    );

    const isLosCauquenes = useMemo(
        () => (
            data?.id === "a3c02b2b-fda7-4e13-be6c-72fa342e4a79" ||      // prd
            data?.id === "__511e8df9-672d-42b1-9711-d82beb7baf64"         // dev
        ),
        [data?.id]
    );

    const isHotelIruna = useMemo(
        () => (
            data?.id === "a15be3ca-5bee-4167-89e2-aaacff0e827e" ||      // prd
            data?.id === "__511e8df9-672d-42b1-9711-d82beb7baf64"         // dev
        ),
        [data?.id]
    );
    const isHotelRivera = useMemo(
        () => (
            data?.id === "fb6185a6-5b1d-4f9a-8db8-60fff4014a6d" ||      // prd
            data?.id === "__511e8df9-672d-42b1-9711-d82beb7baf64"         // dev
        ),
        [data?.id]
    );
    const isHotelCGBeach = useMemo(
        () => (
            data?.id === "44b7b2ff-e90c-4656-9dcf-96f797b6fdba" ||      // prd
            data?.id === "__511e8df9-672d-42b1-9711-d82beb7baf64"         // dev
        ),
        [data?.id]
    );
    const isHotelGrandBrizoLaPlata = useMemo(
        () => (
            data?.id === "57c5ac85-75d3-4f30-9933-bbb12369ce40" ||      // prd
            data?.id === "__511e8df9-672d-42b1-9711-d82beb7baf64"         // dev
        ),
        [data?.id]
    );
    const isBrizoSalta = useMemo(
        () => (
            data?.id === "813161e1-a7a7-4916-953b-6dcf80b4c288" ||      // prd
            data?.id === "__511e8df9-672d-42b1-9711-d82beb7baf64"         // dev
        ),
        [data?.id]
    );
    const isAlvarezArguelles = useMemo(
        () => (
            [
                "__511e8df9-672d-42b1-9711-d82beb7baf64",         // dev
                "f33a83b9-6284-46e6-8469-b5b17de2c9ba", // Brizo Neuquen
                "813161e1-a7a7-4916-953b-6dcf80b4c288", // Brizo Salta
                "c0bb7b5c-cd44-4288-98f1-4868ea4e5c11", // Grand Brizo Comahue
                "1de880e5-bfb6-4518-bd1b-095443bbc58b", // Costa Galana
                "c07d2d8a-9db1-412c-b24d-c0fcc3c5e7a0", // Grand Brizo Bel Air
                "57c5ac85-75d3-4f30-9933-bbb12369ce40", // Grand Brizo La Plata
                "a15be3ca-5bee-4167-89e2-aaacff0e827e", // Hotel Iruna
                "fb6185a6-5b1d-4f9a-8db8-60fff4014a6d", // Hotel Riviera
                "7b550705-bc9d-4412-8296-4de9163d53ae", // Template Brizo
                "08cf09ba-1783-4a5b-8e32-8bffb525cd40", // Template Collections
                "97a5522f-6854-4546-9d93-b35584220285", // Template Grand Brizo
                "44b7b2ff-e90c-4656-9dcf-96f797b6fdba", // CG Beach
                "2645e6bf-25cc-4d87-8bc4-80b01f026fea", // Santa Rosa
                "365edffa-c56e-4918-a16a-305452315e8d", // Grand Brizo Buenos Aires
                "a3c02b2b-fda7-4e13-be6c-72fa342e4a79" // Los Cauquenes
            ].includes(data?.id)
        ),
        [data?.id]
    );

    if (loading) {
        return <div className="text-center" style={{ opacity: 0.3, height: "100%", alignContent: "center" }}>
            <div className="spinner-border" role="status"></div>
        </div>
    }

    let currency = "EUR";
    if (isCostaGalana || isRoyal || isBelAir || isComahue || isBrizoNeuquen || isSantaRosa || isAlvarezArguelles ) {
        currency = "$";
    }
    if (isHostz(data?.id) && !isHostzMyde(data?.id)) {
        currency = "CHF";
    }
    const value = {
        eventVersion,
        lastEvent,

        data: mergedData,
        code: mergedData?.code || "--",
        extra: mergedData?.extra || {},
        languages: mergedData?.languages || ["US", "ES", "DE"],
        theme: mergedData?.settings?.theme ? parseInt(mergedData.settings.theme) : 3,
        sections: normalizedSections,
        placeholders: mergedData?.settings?.placeholders || {},
        sectionsByCode,
        servicesMenu: mergedData?.settings?.servicesMenu || [],
        paymentGateway: mergedData?.payment_gateways,
        cacheVersion: mergedData?.cacheVersion,
        faqs: mergedData?.faqs ? mergedData.faqs.sort((a, b) => a.order - b.order) : [],
        getUrlPath,
        getServicesByType,
        getBookableItems,
        getBookableItem,
        // clearWebpush,
        setData,
        currency,
        refreshCache,
        previewMode,
        previewParentOrigin,
        previewSelectedCode: previewDraft?.selectedCode || null,
        previewSelectedBlockId: previewDraft?.selectedBlockId || null,
        previewHoveredBlockId: previewDraft?.hoveredBlockId || null,
        previewHighlightedBlockId: previewDraft?.hoveredBlockId || previewDraft?.selectedBlockId || null,
        previewLanguage: previewDraft?.previewLanguage || null,
        previewReview: previewDraft?.reviewPreview || null,
    }

    return <HotelContext.Provider value={value}>
        {error && <p>Error: {error}</p>}
        {/* {showRequestPermission && <div id="allow-push-notification-bar" style={{ position: 'fixed', zIndex: 1500, bottom: 0, padding: "15px 20px" }} className={`allow-push-notification-bar ${showRequestPermission === "fix" ? 'blocked' : 'yellow'}`}>
            <div className="content text-center">
                <FontAwesomeIcon size="3x" className="mb-1" icon={faExclamationCircle}/>
                <div className="text">
                    {showRequestPermission === "accept" && <p>Para el correcto uso de la aplicación es recomendable permitir las notificaciones</p>}
                    {showRequestPermission === "fix" && <p>Las notificaciones están bloqueadas, no podrás usar la aplicación como corresponde</p>}
                </div>
                <div className="buttons-more">
                    <button type="button" className="btn btn-primary ok-button button-1" onClick={() => {
                        console.log('regSW 111')
                        if ('safari' in window && 'pushNotification' in window.safari) {
                            console.log(' regSW 222')
                            var permissionData = window.safari.pushNotification.permission('web.com.vicomanager.pwa');
                            console.log('permissionData', permissionData)
                            const userData = {
                                hotelId: data.id,
                                sessionId: 'TODO:::???' // que es esto? sessionId
                            }
                            safariCheckRemotePermission(permissionData, userData);
                        } else {
                            console.log('regSW 333')
                            window.Notification.requestPermission(function(result) {
                                console.log('regSW 444')
                                console.log('regSW result', result)
                                if (result === 'denied') {
                                    console.log('Permission wasn\'t granted. Allow a retry.');
                                    return;
                                } else if (result === 'default') {
                                    console.log('The permission request was dismissed.');
                                    return;
                                }
                                // Hacer algo con el permiso concedido.
                            });
                        }
                        setShowRequestPermission(false);
                        }}>
                        { showRequestPermission === "accept" ? "Permitir Notificaciones" : "Entendido" }
                    </button>
                </div>
            </div>
        </div>} */}
        {mergedData && <div className={`App hotel-${mergedData.code} theme-${mergedData.settings?.theme ?? '3'}${previewMode ? ' preview-mode' : ''}`}>{children}</div>}
    </HotelContext.Provider>
}

export { HotelProvider, HotelContext }